import React, { useEffect, useState } from 'react';
import {
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
} from '@material-ui/core';

import MoreVertIcon from '@material-ui/icons/MoreVert';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import 'translations/translations';
import { ContactDto } from '.generated/api';
import { useTranslation } from 'react-i18next';
import 'theme/iconClasses.css';

import { primaryColor } from 'theme/theme';
import { CreateEditContactDialog } from 'features/organizations/organizationTabsContent/CreateEditContactDialog';
import { organizationActions } from 'actions/organizationActions';
import { notificationActions } from 'actions/notificationActions';

const useStyles = makeStyles((theme) => ({
  alignRight: {
    textAlign: 'right',
  },
  marginRight: {
    marginRight: '0.6rem',
  },
  root: {
    padding: '0.1rem 1rem',
    minWidth: '8.5rem',
  },
  plusIcon: {
    display: 'inline-block',
    fontSize: '1.5rem',
    lineHeight: 0,
    marginRight: '0.5rem',
  },
  addButton: { marginLeft: '0' },
  actionsButton: {
    padding: '0',
  },
  buttonRoot: {
    marginRight: 0,
  },
  tableContainer: {
    marginTop: '1rem',
    marginLeft: '-1.5rem',
    width: 'calc(100% + 3rem)',
  },
  table: {
    color: primaryColor(),
  },
  tableHead: {
    backgroundColor: 'rgba(240, 241, 243, 1)',
    color: primaryColor(),
  },
  tableHeadCell: {
    fontWeight: 'bold',
    color: primaryColor(),
    padding: '0.7rem 1rem 0.5rem 1rem',
  },
  tableCell: {
    color: primaryColor(),
  },
  tableRow: {
    textDecoration: 'none',
  },
  rowInactive: {
    background: 'rgba(240, 241, 243, 1)',
    opacity: '0.6',
  },
  textBold: {
    fontWeight: 'bold',
    color: primaryColor(),
  },
  preDataView: {
    marginTop: '3rem',
    textAlign: 'center',
  },
  error: {
    color: theme.palette.error.main,
  },
  paper: {
    padding: '0',
  },
}));

const ContactsTabTable: React.FC = () => {
  const { t } = useTranslation(['translations']);
  const dispatch = useDispatch();
  const classes = useStyles();
  const organization = useSelector(
    (state: ApplicationState) => state.organization?.organization,
  );
  const [contacts, setContacts] = useState<ContactDto[]>([]);
  const [contact, setContact] = useState<ContactDto>();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  const handleOpenDialog = () => {
    setIsMenuOpen(false);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleRemoveFromList = () => {
    if (contact && organization) {
      dispatch(
        organizationActions.deleteContact(
          organization.organizationId,
          contact.id,
          (error) => {
            setIsMenuOpen(false);
            if (error) {
              dispatch(notificationActions.enqueueErrorResponse(error));
            }
          },
        ),
      );
    }
  };

  const handleShowContextActions = (
    event: React.MouseEvent<HTMLButtonElement>,
    contactRow: ContactDto,
  ) => {
    const tempContact: ContactDto = {
      id: contactRow.id,
      name: contactRow.name ?? '',
      email: contactRow.email ?? '',
      phone: contactRow.phone ?? '',
    };
    setContact(tempContact);
    setAnchorEl(event.currentTarget);
    setIsMenuOpen(true);
  };

  const handleHideContextActions = () => {
    setIsMenuOpen(false);
  };

  const getContacts = () => {
    setContacts(organization?.contacts ?? []);
  };

  useEffect(() => {
    getContacts();
  }, [organization?.contacts]);

  return (
    <>
      <TableContainer className={classes.tableContainer}>
        <Table aria-label="organization table" className={classes.table}>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.tableHeadCell}>
                {t('organization_page.contacts.table.contact_name')}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('organization_page.contacts.table.contact_email')}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('organization_page.contacts.table.contact_phone')}
              </TableCell>
              <TableCell align="right" className={classes.tableHeadCell}>
                {t('common.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts?.map((contactRow: ContactDto, index: number) => (
              <TableRow key={index} hover={true} className={classes.tableRow}>
                <TableCell className={classes.textBold}>
                  {contactRow.name}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {contactRow.email}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {contactRow.phone}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={(e) => handleShowContextActions(e, contactRow)}
                    color="secondary"
                    className={classes.actionsButton}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                  <Menu
                    keepMounted
                    open={isMenuOpen}
                    onClose={handleHideContextActions}
                    anchorEl={anchorEl}
                    classes={{
                      paper: classes.paper,
                    }}
                  >
                    <MenuItem onClick={handleOpenDialog}>
                      {t('common.edit')}
                    </MenuItem>
                    <MenuItem onClick={handleRemoveFromList}>
                      {t('organization_page.contacts.labels.remove_contact')}
                    </MenuItem>
                  </Menu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CreateEditContactDialog
        isOpen={isDialogOpen}
        handleClose={handleCloseDialog}
        contact={contact}
      />
    </>
  );
};

export { ContactsTabTable };
